/** Today as an ISO calendar date (YYYY-MM-DD) in the caller's local time. */
export function todayIsoDate(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;

  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

type LocalDateFormat = "short" | "shortWithYear" | "longWithYear";
type TimestampFormat = "short" | "shortWithYear" | "shortWithYearAndTime";

const LOCAL_DATE_FORMATS: Record<LocalDateFormat, Intl.DateTimeFormatOptions> = {
  short: { month: "short", day: "numeric" },
  shortWithYear: { month: "short", day: "numeric", year: "numeric" },
  longWithYear: { month: "long", day: "numeric", year: "numeric" },
};

const TIMESTAMP_FORMATS: Record<TimestampFormat, Intl.DateTimeFormatOptions> = {
  short: { month: "short", day: "numeric" },
  shortWithYear: { month: "short", day: "numeric", year: "numeric" },
  shortWithYearAndTime: {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
};

export function formatLocalDate(
  isoDate: string | null,
  format: LocalDateFormat = "short"
): string {
  if (!isoDate) return "";

  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;

  return new Date(year, month - 1, day).toLocaleDateString(
    "en-US",
    LOCAL_DATE_FORMATS[format]
  );
}

const DAY_MS = 86_400_000;

/** Parse an ISO calendar date (YYYY-MM-DD) to a UTC epoch-ms at midnight. */
export function isoDateToUtcMs(isoDate: string): number {
  const [year, month, day] = isoDate.split("-").map(Number);

  return Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

/** Render a UTC epoch-ms back to an ISO calendar date (YYYY-MM-DD). */
export function utcMsToIsoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * The Monday that starts the ISO week containing `dateIso` (defaults to today),
 * as an ISO calendar date. Weeks start Monday per the functional-forms contract.
 * Pure and deterministic when given an explicit date.
 */
export function startOfWeekIso(dateIso: string = todayIsoDate()): string {
  const ms = isoDateToUtcMs(dateIso);
  const weekday = new Date(ms).getUTCDay(); // 0=Sun … 6=Sat
  const daysSinceMonday = (weekday + 6) % 7;

  return utcMsToIsoDate(ms - daysSinceMonday * DAY_MS);
}

/**
 * The Sunday that ends the ISO week containing `dateIso` (defaults to today),
 * as an ISO calendar date.
 */
export function endOfWeekIso(dateIso: string = todayIsoDate()): string {
  return utcMsToIsoDate(isoDateToUtcMs(startOfWeekIso(dateIso)) + 6 * DAY_MS);
}

/**
 * The Mondays starting the `count` weeks before the week containing `nowIso`
 * (defaults to today), most recent first. Drives the backfill week picker —
 * the current week is excluded because the "this week" ghost row owns it.
 * Pure and deterministic when given an explicit date.
 */
export function recentPastWeekStarts(
  count: number,
  nowIso: string = todayIsoDate()
): string[] {
  const currentStartMs = isoDateToUtcMs(startOfWeekIso(nowIso));

  return Array.from({ length: count }, (_, index) =>
    utcMsToIsoDate(currentStartMs - (index + 1) * 7 * DAY_MS)
  );
}

/** "Week of {short date}" label for a week-start (or any) ISO date. */
export function weekOfLabel(iso: string): string {
  return `Week of ${formatLocalDate(iso, "short")}`;
}

/**
 * Whole weeks elapsed from `startIso` to `nowIso` (defaults to today), floored.
 * Drives the derived onboarding stage ("Week N since start"); the display layer
 * decides whether to show N or N+1. Can be negative if `startIso` is in the
 * future. Pure and deterministic when given explicit dates.
 */
export function weeksSince(startIso: string, nowIso: string = todayIsoDate()): number {
  const days = Math.floor((isoDateToUtcMs(nowIso) - isoDateToUtcMs(startIso)) / DAY_MS);

  return Math.floor(days / 7);
}

export function formatTimestamp(
  isoTimestamp: string,
  format: TimestampFormat = "short"
): string {
  const date = new Date(isoTimestamp);

  if (format === "short" || format === "shortWithYear") {
    return date.toLocaleDateString("en-US", TIMESTAMP_FORMATS[format]);
  }

  return date.toLocaleString("en-US", TIMESTAMP_FORMATS[format]);
}

/**
 * Compact recency for the workspace sidebar's chat rows: "now", "42m",
 * "3h", "6d", "5w" — matching the rd-workspace comp's right-aligned meta.
 */
export function formatRelativeShort(isoTimestamp: string, nowMs = Date.now()): string {
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return "";

  const minutes = Math.floor((nowMs - then) / 60_000);
  if (minutes < 5) return "now";

  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;

  return `${Math.floor(days / 7)}w`;
}
